// Trash table columns — the domain cells inside the common DataTable: the
// item (type-tiled name + entity label / reference code), who deleted it, when
// it was deleted, and the auto-purge countdown chip. NOTHING is sortable: the
// trash/list body carries no orderBy (the server fixes newest-deleted first),
// so no header may pretend otherwise.

import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon, type DataTableColumn } from '@/components/common';
import type { TrashRow } from '@/api/endpoints/trashApi';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import { activityDateParts } from '@/pages/activity-log-page/activityMeta';
import {
  ITEM_TYPE_META,
  itemTypeLabel,
  purgeChipVariant,
  trashRowCode,
  trashRowName,
} from './trashMeta';

const dash = <span className='trash-table__dash'>—</span>;

export const useTrashColumns = (): DataTableColumn<TrashRow>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'item',
      label: t('trash_col_item'),
      width: 'minmax(16rem, 2fr)',
      render: (row) => {
        const name = trashRowName(row);
        const code = trashRowCode(row);
        return (
          <span className='trash-table__target'>
            <span className='trash-table__tile' aria-hidden='true'>
              <Icon name={ITEM_TYPE_META[row.itemType].icon} size={16} />
            </span>
            <span className='trash-table__target-text'>
              <span className='trash-table__target-name'>{name ?? dash}</span>
              <span className='trash-table__target-sub'>
                {itemTypeLabel(row.itemType, t)}
                {code && (
                  <>
                    <span className='trash-table__target-dot'>·</span>
                    {code}
                  </>
                )}
              </span>
            </span>
          </span>
        );
      },
    },
    {
      key: 'deletedBy',
      label: t('trash_col_deleted_by'),
      width: 'minmax(12rem, 1fr)',
      render: (row) =>
        row.deletedBy ? (
          <span className='trash-table__person'>
            <Avatar
              initials={initialsOf(row.deletedBy.fullName)}
              size={32}
              tone={toneOf(row.deletedBy.fullName)}
            />
            <span className='trash-table__person-name'>{row.deletedBy.fullName}</span>
          </span>
        ) : (
          dash
        ),
    },
    {
      key: 'deletedAt',
      label: t('trash_col_deleted_at'),
      render: (row) => {
        const { day, time } = activityDateParts(row.deletedAt, t);
        return (
          <span className='trash-table__date'>
            <span className='trash-table__date-day'>{day}</span>
            {time && <span className='trash-table__date-time'>{time}</span>}
          </span>
        );
      },
    },
    {
      key: 'purge',
      label: t('trash_col_purge'),
      align: 'end',
      render: (row) => (
        <Chip variant={purgeChipVariant(row.purgeInDays)}>
          {t('trash_purge_days', { count: row.purgeInDays })}
        </Chip>
      ),
    },
  ];
};
